import classNames from "classnames";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import Table from "components/DesignSystem/Table/V2/Table";
import Wind from "components/icons/Wind";
import { useQuery } from "hooks/useQuery";
import { flexRender, getCoreRowModel, useReactTable } from "react-table-8.10.7";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import {
  EntityWithSeasonAndCount,
  TaskListBaseQueryParams,
} from "types/Models/task";
import { getEntityTaskTableColumns } from "./EntityTaskTableColumns";
import { TaskTableBodyPerSeasonWrapper } from "./TaskTableBodyPerSeasonWrapper";

type EntityTaskTableProps = {
  entityId: string;
  isParentFetching: boolean;
  isSingleEntity?: boolean;
  isAnyFilterActive: boolean;
  resetFilter: () => void;
  taskSeasonsWithEntity: EntityWithSeasonAndCount;
  params: TaskListBaseQueryParams;
};

export const TaskSkeletonComponent = () => (
  <div className="t-flex sm:t-gap-2 sm:t-px-4">
    <Skeleton
      className="t-h-6 t-mr-12 sm:t-mr-0"
      style={{ flex: "277 1 0%" }}
    />
    <Skeleton className="t-h-6 t-mr-6 sm:t-mr-0" style={{ flex: "71 1 0%" }} />
    <Skeleton className="t-h-6 t-mr-8 sm:t-mr-0" style={{ flex: "127 1 0%" }} />
    <Skeleton className="t-h-6 t-mr-6 sm:t-mr-0" style={{ flex: "91 1 0%" }} />
    <Skeleton className="t-h-6 t-mr-4 sm:t-mr-0" style={{ flex: "91 1 0%" }} />
    <Skeleton className="t-h-6" style={{ flex: "22 1 0%" }} />
  </div>
);

export const EntityTaskTable = ({
  entityId,
  isParentFetching,
  isAnyFilterActive,
  resetFilter,
  isSingleEntity,
  taskSeasonsWithEntity,
  params,
}: EntityTaskTableProps) => {
  const query = useQuery();
  const { isCpa } = useRoleBasedView();
  const isLaunchingTask = Boolean(query.get("check_for_tasks"));

  const table = useReactTable({
    data: [],
    columns: getEntityTaskTableColumns(isCpa),
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 5,
    },
    enableRowSelection: true,
  });

  const tasksPerSeason = Object.entries(
    taskSeasonsWithEntity.season_task_counts
  );
  const isEmpty = tasksPerSeason.every(([, taskCount]) => taskCount === 0);
  const isLoadingAndTaskNotLaunching = !isLaunchingTask && isParentFetching;

  return (
    <Async.Root
      isLoading={isLoadingAndTaskNotLaunching}
      isSuccess={true}
      isEmpty={isEmpty}
      renderOnLoad={
        <div className="t-space-y-8 t-h-full t-w-full t-my-4">
          <TaskSkeletonComponent />
          <TaskSkeletonComponent />
          <TaskSkeletonComponent />
        </div>
      }
    >
      <Async.Empty>
        <div
          className={classNames(
            "t-flex t-flex-col t-bg-surface-lighter-grey t-rounded-md t-justify-center t-items-center t-w-full",
            {
              "t-py-28": isSingleEntity,
              "t-py-8": !isSingleEntity,
            }
          )}
        >
          <Wind className="t-text-neutral-40" />
          <div className="t-text-subtext t-text-text-60 t-mt-4">
            {isAnyFilterActive ? "No filings found" : "No filings to show"}
          </div>
          <div className="t-text-body-sm t-text-text-30 t-mb-4 t-px-4 t-text-center">
            {isAnyFilterActive
              ? "Try adjusting or clearing your filters to see more results"
              : "You have nothing to view here"}
          </div>
          {isAnyFilterActive && (
            <Button size="small" onClick={resetFilter}>
              Reset to default view
            </Button>
          )}
        </div>
      </Async.Empty>
      <Async.Success>
        <Table.Container
          sticky
          className={classNames({
            "t-h-full": isSingleEntity,
          })}
        >
          <Table.Content>
            <Table.Head className="sm:t-hidden">
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.HeadRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.HeadCell
                      key={header.id}
                      style={{
                        width: `${header.getSize()}%`,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Table.HeadCell>
                  ))}
                </Table.HeadRow>
              ))}
            </Table.Head>
            <Table.Body>
              {tasksPerSeason
                .sort(
                  ([seasonA], [seasonB]) => Number(seasonB) - Number(seasonA)
                )
                .filter(([_, tasksCount]) => tasksCount > 0)
                .map(([season]) => (
                  <TaskTableBodyPerSeasonWrapper
                    key={season}
                    isParentFetching={isParentFetching}
                    season={season.toString()}
                    entityId={entityId}
                    params={params}
                  />
                ))}
            </Table.Body>
          </Table.Content>
        </Table.Container>
      </Async.Success>
    </Async.Root>
  );
};
